import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileDown, Settings, User, Package, Plug, LogOut } from 'lucide-react';
import { FloorPlan, LightingFixture } from '@/types/floorPlanViewer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FloorPlanSharingModal } from '../../FloorPlanSharingModal';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useAuth as useSupabaseAuth } from '@/components/auth/AuthProvider';
import { useAuth as useClerkAuth, useUser, useClerk } from '@clerk/clerk-react';
// VF-40: the ~2400-line ExportPreviewModal (and its export dependencies) was
// statically imported into the always-loaded header. Lazy-load it so the chunk
// is only fetched the first time the user opens Export.
const ExportPreviewModal = lazy(() =>
  import('./ExportPreviewModal').then(m => ({ default: m.ExportPreviewModal })),
);
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useAppStore } from '@/stores/appStore';
import { UndoRedoIndicator } from './UndoRedoIndicator';
import { ActiveUsersIndicator } from '../../ActiveUsersIndicator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { buildFloorPlanReturnUrl, rememberFloorPlanReturnUrl } from '@/utils/navigationReturn';

interface FloorPlanHeaderProps {
  floorPlan: FloorPlan;
  onBack?: () => void;
  isPublicView?: boolean;
  viewMode?: 'v2' | 'original' | 'sideBySide';
  onViewModeChange?: (mode: 'v2' | 'original' | 'sideBySide') => void;
  activeUsers?: Array<{
    id: string;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
    last_active: string;
    isViewer?: boolean;
  }>;
}

export function FloorPlanHeader({
  floorPlan,
  onBack,
  isPublicView,
  viewMode = 'v2',
  onViewModeChange,
  activeUsers = [],
}: FloorPlanHeaderProps) {
  const navigate = useNavigate();
  const floorPlanReturnUrl = buildFloorPlanReturnUrl(floorPlan.id);

  // Check if using Clerk
  const isUsingClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  // Always call both hooks (React rules of hooks)
  const { userId: clerkUserId } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { clerk } = useClerk();
  const supabaseAuth = useSupabaseAuth();
  const { user: supabaseUser } = supabaseAuth;

  // Determine which auth system is being used
  const user = isUsingClerk ? clerkUser : supabaseUser;
  const userEmail = isUsingClerk
    ? clerkUser?.primaryEmailAddress?.emailAddress || ''
    : supabaseUser?.email || '';

  // Sign out handler
  const handleSignOut = async () => {
    try {
      logger.log('Attempting to sign out');

      if (isUsingClerk && clerk) {
        await clerk.signOut({ redirectUrl: '/' });
      } else {
        await supabaseAuth.signOut();
        navigate('/');
      }

      logger.log('Sign out successful');
      toast.success('Signed out successfully');
    } catch (error) {
      logger.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  const [sharingOpen, setSharingOpen] = useState(false);
  const [hasActiveShareLink, setHasActiveShareLink] = useState(false);

  useEffect(() => {
    rememberFloorPlanReturnUrl(floorPlan.id);
  }, [floorPlan.id]);
  const [exportOpen, setExportOpen] = useState(false);
  const { data } = useFloorPlanContext();
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const aiTools = useAppStore(state => state.aiTools);

  // Filter fixtures by confidence threshold for export (same as app display)
  const filteredFixtures = (data.fixtures || []).filter(
    (fixture: LightingFixture) => (fixture.confidence_score ?? 1) >= aiTools.confidenceThreshold,
  );

  // Debug logging for confidence threshold filtering
  logger.log(`[Export] Confidence threshold: ${aiTools.confidenceThreshold}`);
  logger.log(`[Export] Total fixtures: ${data.fixtures?.length ?? 0}`);
  logger.log(`[Export] Filtered fixtures: ${filteredFixtures.length}`);
  if (data.fixtures?.length > 0 && filteredFixtures.length === 0) {
    logger.warn('[Export] WARNING: All fixtures filtered out by confidence threshold!');
    logger.log(
      '[Export] Sample fixture scores:',
      (data.fixtures || []).slice(0, 5).map((f: LightingFixture) => ({
        id: f.id,
        score: f.confidence_score,
        passesThreshold: (f.confidence_score ?? 1) >= aiTools.confidenceThreshold,
      })),
    );
  }

  // Check for active share link
  useEffect(() => {
    const checkActiveShareLink = async () => {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data } = await supabase
        .from('floor_plans')
        .select('public_link_token, public_link_expires_at')
        .eq('id', floorPlan.id)
        .single();

      const hasActive =
        data?.public_link_token &&
        data.public_link_expires_at &&
        new Date(data.public_link_expires_at) > new Date();
      setHasActiveShareLink(!!hasActive);
    };

    if (!isPublicView) {
      checkActiveShareLink();
    }
  }, [floorPlan.id, sharingOpen, isPublicView]);

  return (
    <div className="bg-white/95 border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur">
      <div className="px-5 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button
                onClick={onBack}
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Go back"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/vfloow-logo.png" alt="vfloow" className="h-8 w-auto" />
              <div className="h-6 w-px bg-border" /> {/* Divider */}
            </div>

            {/* Floor plan name - centered on desktop, left-aligned on mobile */}
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold">{floorPlan.name}</h2>

              {/* View mode selector */}
              {onViewModeChange && (
                <Tabs
                  value={viewMode}
                  onValueChange={value => onViewModeChange(value as any)}
                  className="hidden md:block"
                >
                  <TabsList>
                    <TabsTrigger value="v2">V2 (New)</TabsTrigger>
                    <TabsTrigger value="original">Original</TabsTrigger>
                    <TabsTrigger value="sideBySide">Side by Side</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Active Users Indicator - show for both editors and viewers */}
            {activeUsers.length > 0 && (
              <>
                <ActiveUsersIndicator users={activeUsers} />
                <div className="w-px h-6 bg-border" />
              </>
            )}

            {!isPublicView && (
              <TooltipProvider delayDuration={500}>
                <>
                  <UndoRedoIndicator floorPlanId={floorPlan.id} />
                  <div className="w-px h-6 bg-border" />

                  {/* TODO: Re-enable share button after fixing viewport coordinate transform issue */}
                  {/* <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSharingOpen(true)}
                        className="relative h-9 w-9"
                      >
                        <LinkIcon className="h-4 w-4" />
                        {hasActiveShareLink && (
                          <Badge
                            className="absolute -top-1 -right-1 h-2 w-2 p-0 bg-green-500"
                            aria-label="Active share link"
                          />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Share floor plan</p>
                    </TooltipContent>
                  </Tooltip> */}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          logger.log(
                            '[Export] Opening modal with opacity:',
                            data.floorPlanMetadata?.image_opacity,
                          );
                          setExportOpen(true);
                        }}
                        className="h-9 w-9"
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export as PDF or PNG</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        aria-label="Settings"
                        onClick={() => {
                          setActiveDrawer('settings');
                          const { setActiveTool } = useAppStore.getState();
                          setActiveTool('select');
                        }}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Settings</p>
                    </TooltipContent>
                  </Tooltip>

                  {/* User Menu / Sign Out */}
                  {isUsingClerk && (
                    <>
                      <div className="w-px h-6 bg-border" />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-9 w-9">
                            <User className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[200px]">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate('/product-library', { state: { from: floorPlanReturnUrl } })
                            }
                            className="cursor-pointer gap-2"
                          >
                            <Package className="w-4 h-4" />
                            <span>Product Library</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate('/integrations', { state: { from: floorPlanReturnUrl } })
                            }
                            className="cursor-pointer gap-2"
                          >
                            <Plug className="w-4 h-4" />
                            <span>Integrations</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={handleSignOut}
                            className="text-destructive cursor-pointer gap-2 focus:text-destructive"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Sign Out</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Mobile view mode selector */}
      {onViewModeChange && (
        <div className="md:hidden px-4 pb-3">
          <Tabs
            value={viewMode}
            onValueChange={value => onViewModeChange(value as any)}
            className="w-full"
          >
            <TabsList className="w-full">
              <TabsTrigger value="v2" className="flex-1">
                V2
              </TabsTrigger>
              <TabsTrigger value="original" className="flex-1">
                Original
              </TabsTrigger>
              <TabsTrigger value="sideBySide" className="flex-1">
                Side by Side
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Sharing Modal - show if user is authenticated */}
      {(user || clerkUserId) && (
        <FloorPlanSharingModal
          open={sharingOpen}
          onClose={() => setSharingOpen(false)}
          floorPlanId={floorPlan.id}
          userEmail={userEmail}
          appUrl={import.meta.env.VITE_PUBLIC_APP_URL || window.location.origin}
        />
      )}

      {/* Export Preview Modal — mounted (and its chunk fetched) only when opened */}
      {exportOpen && (
        <Suspense fallback={null}>
          <ExportPreviewModal
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            floorPlan={floorPlan}
            fixtures={filteredFixtures}
            circuits={data.circuits || []}
            controls={data.controls || []}
            rooms={data.rooms || []}
            products={data.lvProducts || []}
            imageOpacity={data.floorPlanMetadata?.image_opacity}
            imageUrl={data.imageUrl}
            markerOpacity={(aiTools.markerOpacity ?? 1) * 100}
            markerSizes={data.floorPlanMetadata?.marker_sizes}
          />
        </Suspense>
      )}
    </div>
  );
}
